import React, { useState, useRef, useEffect, useCallback } from "react";
import "./HeaderSelect.css";

const ChevronIcon = () => (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
        <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const SearchIcon = () => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <circle cx="5.5" cy="5.5" r="4" stroke="currentColor" strokeWidth="1.5" />
        <path d="M9 9L13 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
);

const CheckIcon = () => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <path d="M3 7L6 10L11 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const HeaderSelect = ({
    options = [],
    value,
    onChange,
    searchable = false,
    placeholder = "Select...",
    searchPlaceholder = "Search...",
    label,
    icon,
    className = "",
    align = "right",
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState("");
    const containerRef = useRef(null);
    const searchRef = useRef(null);

    const close = useCallback(() => {
        setIsOpen(false);
        setSearch("");
    }, []);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                close();
            }
        };
        const handleEscape = (e) => {
            if (e.key === "Escape") close();
        };
        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("keydown", handleEscape);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", handleEscape);
        };
    }, [close]);

    useEffect(() => {
        if (isOpen && searchable && searchRef.current) {
            setTimeout(() => searchRef.current?.focus(), 50);
        }
    }, [isOpen, searchable]);

    const filtered =
        searchable && search
            ? options.filter((o) => o.label.toLowerCase().includes(search.toLowerCase()))
            : options;

    const displayLabel = value?.label || placeholder;

    return (
        <div className={`hs ${className}`} ref={containerRef}>
            {label && <span className="hs-label">{label}</span>}
            <button
                className={`hs-trigger ${isOpen ? "hs-trigger--open" : ""}`}
                onClick={() => setIsOpen(!isOpen)}
                type="button"
            >
                {icon && <span className="hs-trigger__icon">{icon}</span>}
                <span className="hs-trigger__text">{displayLabel}</span>
                <ChevronIcon />
            </button>
            {isOpen && (
                <div className={`hs-panel hs-panel--${align}`}>
                    {searchable && (
                        <div className="hs-panel__search">
                            <SearchIcon />
                            <input
                                ref={searchRef}
                                className="hs-panel__search-input"
                                type="text"
                                placeholder={searchPlaceholder}
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                    )}
                    <div className="hs-panel__list">
                        {filtered.length === 0 ? (
                            <div className="hs-panel__empty">No results</div>
                        ) : (
                            filtered.map((option) => (
                                <button
                                    key={String(option.value)}
                                    className={`hs-option ${value?.value === option.value ? "hs-option--active" : ""}`}
                                    onClick={() => {
                                        onChange(option);
                                        close();
                                    }}
                                    type="button"
                                >
                                    <span className="hs-option__text">{option.label}</span>
                                    {value?.value === option.value && (
                                        <span className="hs-option__check">
                                            <CheckIcon />
                                        </span>
                                    )}
                                </button>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default HeaderSelect;
